// Main entry point that imports and registers all components
import './styles/global.scss';

import { render } from 'preact';

import { CodeListComponent } from './codes/components/code-list.component';
import { CollectiblesComponent } from './content/components/collectibles/collectibles.component';
import { extractCollectiblesAttributes } from './content/components/collectibles/collectibles.types';
import { LoginComponent } from './content/components/login/login.component';
import { extractLoginAttributes } from './content/components/login/login.types';
import { NeedAccessComponent } from './content/components/need-access/need-access.component';
import { extractNeedAccessAttributes } from './content/components/need-access/need-access.types';
import { PromotedProductsComponent } from './content/components/promoted-products/promoted-products.component';
import { extractPromotedProductsAttributes } from './content/components/promoted-products/promoted-products.types';
import { VideoComponent } from './content/components/video/video.component';
import { extractVideoAttributes } from './content/components/video/video.types';
import { VideoBarComponent } from './content/components/video-bar/video-bar.component';
import { extractVideoBarAttributes } from './content/components/video-bar/video-bar.types';
import { VideoLandingComponent } from './content/components/video-landing/video-landing.component';
import { loadFeatureFlags } from './feature-flags/store/feature-flags.thunk';
import { initLocale } from './i18n';
import { MembershipAccountInfoWithStore } from './membership/components/membership-account-info/membership-account-info.component';
import { extractMembershipAccountInfoAttributes } from './membership/components/membership-account-info/membership-account-info.types';
import { MembershipJoinWithStore } from './membership/components/membership-join/membership-join.component';
import { extractMembershipJoinAttributes } from './membership/components/membership-join/membership-join.types';
import { MembershipManagementWithStore } from './membership/components/membership-management/membership-management.component';
import { extractMembershipManagementAttributes } from './membership/components/membership-management/membership-management.types';
import { MembershipPassDownloadWithStore } from './membership/components/membership-pass-download/membership-pass-download.component';
import { extractMembershipPassDownloadAttributes } from './membership/components/membership-pass-download/membership-pass-download.types';
import { TracklistAppBlockComponent } from './music/components/tracklist-app-block/tracklist-app-block.component';
import { extractTracklistAppBlockAttributes, TRACKLIST_APP_BLOCK_OBSERVED_ATTRIBUTES } from './music/components/tracklist-app-block/tracklist-app-block.types';
import { PerksComponent } from './perks/components/perks/perks.component';
import { PerksPostSubmission } from './perks/components/perks-post-submission/perks-post-submission.component';
import { extractPerksAttributes } from './perks/components/perks/perks.types';
import { ProductFormFallbackComponent } from './product-form/components/product-form-fallback/product-form-fallback.component';
import { ProductFormLegalTermsComponent } from './product-form/components/product-form-legal-terms/product-form-legal-terms.component';
import { ProductFormWrapperComponent } from './product-form/components/product-form-wrapper/product-form-wrapper.component';
import { extractProductFormWrapperAttributes } from './product-form/components/product-form-wrapper/product-form-wrapper.types';
import { store } from './store';
import { Toasts } from './toast/toast.component';

/* ==============================
| Bootstrap
 ==============================*/
initLocale();
store.dispatch(loadFeatureFlags());

// Product Form Fallback
if (window.location.pathname.includes('/products/')) {
    if (!document.getElementsByTagName('single-product-form-wrapper-v2').length) {
        const element = document.createElement('single-product-form-fallback-v2');
        document.body.appendChild(element);
    }
}
/* ==============================
| End Bootstrap
 ==============================*/

// Wait for vendor bundle to be ready
const waitForVendor = (): Promise<void> => {
  return new Promise((resolve) => {
    if (
      typeof window !== 'undefined' &&
      (window as any).SingleVendor?.preact &&
      (window as any).SingleVendor?.qrcode
    ) {
      resolve();
    } else {
      setTimeout(() => waitForVendor().then(resolve), 10);
    }
  });
};

// Custom element definitions
class SingleCodeListElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const keyIds = this.getAttribute('key-ids');
      render(<CodeListComponent keyIds={keyIds} />, this);
    });
  }
}

class SingleVideoElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractVideoAttributes(this);
      render(<VideoComponent {...(props as any)} />, this);
    });
  }
}

class SinglePromotedProductsElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractPromotedProductsAttributes(this);
      render(<PromotedProductsComponent {...(props as any)} />, this);
    });
  }
}

class SingleVideoBarElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractVideoBarAttributes(this);
      render(<VideoBarComponent {...(props as any)} />, this);
    });
  }
}

class SingleVideoLandingElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      render(<VideoLandingComponent />, this);
    });
  }
}

class SingleCollectiblesElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractCollectiblesAttributes(this);
      render(<CollectiblesComponent {...(props as any)} />, this);
    });
  }
}

class SingleLoginElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractLoginAttributes(this);
      render(<LoginComponent {...(props as any)} />, this);
    });
  }
}

class SingleNeedAccessElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractNeedAccessAttributes(this);
      render(<NeedAccessComponent {...(props as any)} />, this);
    });
  }
}

class SinglePerksElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractPerksAttributes(this);
      render(<PerksComponent {...(props as any)} />, this);
    });
  }
}

class SinglePostSubmissionElement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      render(<PerksPostSubmission />, this);
    });
  }
}

class SingleMembershipManagement extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractMembershipManagementAttributes(this);
      render(<MembershipManagementWithStore {...(props as any)} />, this);
    });
  }
}

class SingleMembershipAccountInfo extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractMembershipAccountInfoAttributes(this);
      render(<MembershipAccountInfoWithStore {...(props as any)} />, this);
    });
  }
}

class SingleMembershipJoin extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractMembershipJoinAttributes(this);
      render(<MembershipJoinWithStore {...(props as any)} />, this);
    });
  }
}

class SingleMembershipPassDownload extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractMembershipPassDownloadAttributes(this);
      render(<MembershipPassDownloadWithStore {...(props as any)} />, this);
    });
  }
}

class SingleProductFormWrapper extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const props = extractProductFormWrapperAttributes(this);
      render(<ProductFormWrapperComponent {...(props as any)} />, this);
    });
  }
}

class SingleProductFormFallback extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      render(<ProductFormFallbackComponent />, this);
    });
  }
}

class SingleProductFormLegalTerms extends HTMLElement {
  connectedCallback(): void {
    waitForVendor().then(() => {
      const legalTerms = this.getAttribute('legal-terms') || '';
      render(<ProductFormLegalTermsComponent legalTerms={legalTerms} />, this);
    });
  }
}

class SingleTracklistAppBlockElement extends HTMLElement {
  private _connected = false;
  private _childGuard: MutationObserver | null = null;
  private _remountQueued = false;

  static get observedAttributes() {
    return TRACKLIST_APP_BLOCK_OBSERVED_ATTRIBUTES;
  }

  private _render() {
    const props = extractTracklistAppBlockAttributes(this);
    render(<TracklistAppBlockComponent {...(props as any)} />, this);
  }

  /**
   * Morphing themes (Horizon et al.) diff a section against the server's
   * HTML on variant selection. The server renders this element empty, so a
   * morph can wipe the Preact-rendered children without ever disconnecting
   * the element — no lifecycle callback fires and the tracklist silently
   * vanishes. The liquid marks the element data-skip-subtree-update for
   * morphs that honor it (Horizon does); this guard catches the rest by
   * remounting when our children disappear while still connected. The
   * remount re-adopts the shared audio engine, so playback is unaffected.
   */
  private _watchForMorphWipe() {
    this._childGuard?.disconnect();
    this._childGuard = new MutationObserver(() => {
      if (!this._connected || !this.isConnected) return;
      if (this.childElementCount > 0 || this._remountQueued) return;
      this._remountQueued = true;
      queueMicrotask(() => {
        this._remountQueued = false;
        if (!this._connected || !this.isConnected || this.childElementCount > 0) return;
        render(null, this);
        this._render();
      });
    });
    this._childGuard.observe(this, { childList: true });
  }

  connectedCallback(): void {
    waitForVendor().then(() => {
      // Theme section re-renders (e.g. variant selection) can detach this
      // element while the vendor poll is pending — don't mount into a
      // disconnected node.
      if (!this.isConnected) return;
      this._connected = true;
      this._render();
      this._watchForMorphWipe();
    });
  }

  disconnectedCallback(): void {
    this._childGuard?.disconnect();
    this._childGuard = null;
    render(null, this);
    this._connected = false;
  }

  attributeChangedCallback(): void {
    if (this._connected) this._render();
  }
}

// Register the custom elements after vendor is ready
waitForVendor().then(() => {
  if (!customElements.get('single-codes')) customElements.define('single-codes', SingleCodeListElement);
  if (!customElements.get('single-video-v2')) customElements.define('single-video-v2', SingleVideoElement);
  if (!customElements.get('single-promoted-products-v2')) customElements.define('single-promoted-products-v2', SinglePromotedProductsElement);
  if (!customElements.get('single-video-bar-v2')) customElements.define('single-video-bar-v2', SingleVideoBarElement);
  if (!customElements.get('single-video-landing-v2')) customElements.define('single-video-landing-v2', SingleVideoLandingElement);
  if (!customElements.get('single-collectibles-v2')) customElements.define('single-collectibles-v2', SingleCollectiblesElement);
  if (!customElements.get('single-login-v2')) customElements.define('single-login-v2', SingleLoginElement);
  if (!customElements.get('single-need-access-v2')) customElements.define('single-need-access-v2', SingleNeedAccessElement);
  if (!customElements.get('single-perks-v2')) customElements.define('single-perks-v2', SinglePerksElement);
  if (!customElements.get('single-post-submission-v2')) customElements.define('single-post-submission-v2', SinglePostSubmissionElement);
  if (!customElements.get('single-membership-management-v2')) customElements.define('single-membership-management-v2', SingleMembershipManagement);
  if (!customElements.get('single-membership-account-info-v2')) customElements.define('single-membership-account-info-v2', SingleMembershipAccountInfo);
  if (!customElements.get('single-membership-join-v2')) customElements.define('single-membership-join-v2', SingleMembershipJoin);
  if (!customElements.get('single-membership-pass-download-v2')) customElements.define('single-membership-pass-download-v2', SingleMembershipPassDownload);
  if (!customElements.get('single-product-form-wrapper-v2')) customElements.define('single-product-form-wrapper-v2', SingleProductFormWrapper);
  if (!customElements.get('single-product-form-fallback-v2')) customElements.define('single-product-form-fallback-v2', SingleProductFormFallback);
  if (!customElements.get('single-product-form-legal-terms-v2')) customElements.define('single-product-form-legal-terms-v2', SingleProductFormLegalTerms);
  if (!customElements.get('single-tracklist-app-block-v2')) customElements.define('single-tracklist-app-block-v2', SingleTracklistAppBlockElement);

  // Global toast container so toastService works on every page,
  // not just pages that host a component rendering <Toasts /> itself.
  if (!document.getElementById('single-toast-container')) {
    const toastHost = document.createElement('div');
    toastHost.id = 'single-toast-container';
    document.body.appendChild(toastHost);
    render(<Toasts />, toastHost);
  }
});
